<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import LineChart from 'shared/components/charts/LineChart.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import '../styles/qaShell.css';

const QA_TRENDS_V2 = 'QA_TRENDS_V2';

const { t } = useI18n();
const store = useStore();

// Validated categorical palette (dataviz slots 1-3: blue / orange / aqua).
const C = {
  confirmed: '#2a78d6',
  passed: '#eb6834',
  pending: '#1baf7a',
  accent: '#0f766e',
};

const isLoading = ref(false);
const errorText = ref('');
const data = ref(null);
const compileMarker = computed(() => QA_TRENDS_V2);

const TREND_COLS = [
  { key: 'review_confirmed', label: 'M_REVIEW_CONFIRMED', fmt: 'num' },
  { key: 'review_passed', label: 'M_REVIEW_PASSED', fmt: 'num' },
  { key: 'review_pending', label: 'M_REVIEW_PENDING', fmt: 'num' },
  // false_positives is not a column: the server sets it to the same number as
  // review_passed on purpose (a false positive IS a flag a reviewer overturned),
  // so it printed the same figure twice under two names.
  { key: 'answer_accuracy_rate', label: 'M_ANSWER_ACCURACY', fmt: 'pct' },
  { key: 'customer_turns', label: 'M_CUSTOMER_TURNS', fmt: 'num' },
  { key: 'ai_tokens', label: 'M_AI_TOKENS', fmt: 'num' },
  { key: 'ai_cost_usd', label: 'M_AI_COST', fmt: 'money' },
  { key: 'agent_count', label: 'M_AGENT_COUNT', fmt: 'num' },
];

const LB_COLS = [
  { key: 'answer_accuracy_rate', label: 'LB_ACCURACY', fmt: 'pct' },
  { key: 'first_response_p50', label: 'LB_FRT_P50', fmt: 'num' },
  { key: 'first_response_p95', label: 'LB_FRT_P95', fmt: 'num' },
  { key: 'response_eligible', label: 'LB_ELIGIBLE', fmt: 'num' },
  { key: 'unassigned_breaches', label: 'LB_BREACHES', fmt: 'num' },
];

const toLocalInput = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const filters = reactive({
  window_start: toLocalInput(Date.now() - 30 * 24 * 60 * 60 * 1000),
  window_end: toLocalInput(Date.now()),
  report_kind: 'overview',
  sort_metric: 'answer_accuracy_rate',
});

const agents = computed(() => store.getters['agents/getAgents'] || []);
const agentName = id => {
  const agent = agents.value.find(a => Number(a.id) === Number(id));
  if (!agent) return `#${id}`;
  return agent.name || agent.available_name || agent.email || `#${id}`;
};

const fmtVal = (value, kind) => {
  if (value === null || value === undefined || value === '') return '—';
  if (kind === 'pct') return `${(Number(value) * 100).toFixed(1)}%`;
  if (kind === 'money') return `$${Number(value).toFixed(4)}`;
  return Number(value);
};

// The denominator behind the accuracy rate. Nil when the day measured nothing,
// in which case the rate is nil too and the tile already reads "—" — there is
// nothing to qualify.
const accuracyDenominator = point =>
  Number(point?.metrics?.answer_accuracy_eligible) || 0;

const trends = computed(() => data.value?.trends || null);
const leaderboard = computed(() => data.value?.leaderboard || null);
const series = computed(() => trends.value?.series || []);
const latest = computed(() =>
  series.value.length ? series.value[series.value.length - 1] : null
);
const lbRows = computed(() => leaderboard.value?.rows || []);
const lbScope = computed(() => leaderboard.value?.scope || {});
const isScoped = computed(() => lbScope.value.scoped === true);
const isEmptyScope = computed(() => lbScope.value.empty_scope === true);
const sortOptions = computed(
  () => leaderboard.value?.sort_metrics_available || ['answer_accuracy_rate']
);

// ── charts ────────────────────────────────────────────────────────────────
const dateLabels = computed(() => series.value.map(p => p.business_date));
const lineDs = (key, color, labelKey) => ({
  label: t(`QA_DASHBOARD.TRENDS.${labelKey}`),
  data: series.value.map(p => p.metrics[key] ?? 0),
  borderColor: color,
  backgroundColor: color,
  tension: 0.3,
  borderWidth: 2,
  pointRadius: 4,
  pointHoverRadius: 6,
});
const reviewChart = computed(() => ({
  labels: dateLabels.value,
  datasets: [
    lineDs('review_confirmed', C.confirmed, 'M_REVIEW_CONFIRMED'),
    lineDs('review_passed', C.passed, 'M_REVIEW_PASSED'),
    lineDs('review_pending', C.pending, 'M_REVIEW_PENDING'),
  ],
}));
const lineOptions = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: { usePointStyle: true, boxWidth: 8 },
    },
  },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
};

// A day only reaches this page once its audit run is final. On this account the
// runs went final from 3 August, so a month-long window answers with eleven
// points and looked like twenty days of data had gone missing. Counted off the
// window the server reports rather than the filter fields, so it always
// describes the series actually on screen and not one the user has half-typed.
const daysInWindow = computed(() => {
  const w = trends.value?.window;
  if (!w?.start || !w?.end) return 0;
  const from = new Date(w.start);
  const to = new Date(w.end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;
  return Math.max(1, Math.round((to - from) / 86_400_000));
});

// Two of the four rankable metrics are empty on this account — including the
// one the page opens on — and a bar chart of nulls draws as a blank frame with
// nothing to say for itself. Which metrics have data is a property of the
// account, not something to hard-code, so it is read off the rows.
const metricsWithData = computed(() =>
  (sortOptions.value || []).filter(metric =>
    lbRows.value.some(row => {
      const value = row?.metrics?.[metric];
      return value !== null && value !== undefined;
    })
  )
);

// The metric the rows on screen were fetched with, not the one in the select —
// changing the select reloads, and until that lands the two disagree.
const loadedSortMetric = ref('');
const selectedMetricHasData = computed(() =>
  metricsWithData.value.includes(loadedSortMetric.value)
);

const lbValue = row => {
  const raw = row.metrics[filters.sort_metric];
  if (raw === null || raw === undefined) return 0;
  return filters.sort_metric === 'answer_accuracy_rate'
    ? Math.round(Number(raw) * 1000) / 10
    : Number(raw);
};
const lbChart = computed(() => ({
  labels: lbRows.value.map(r => agentName(r.agent_user_id)),
  datasets: [
    {
      label: t(`QA_DASHBOARD.TRENDS.SORT_${filters.sort_metric}`),
      data: lbRows.value.map(lbValue),
      backgroundColor: C.confirmed,
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
  ],
}));
const lbOptions = computed(() => ({
  indexAxis: 'y',
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { x: { beginAtZero: true } },
}));

const load = async () => {
  const startDate = new Date(filters.window_start);
  const endDate = new Date(filters.window_end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    errorText.value = t('QA_DASHBOARD.TRENDS.INVALID_RANGE');
    return;
  }
  isLoading.value = true;
  errorText.value = '';
  try {
    const { data: body } = await QaDashboardApi.getTrends({
      window_start: startDate.toISOString(),
      window_end: endDate.toISOString(),
      report_kind: filters.report_kind,
      sort_metric: filters.sort_metric,
    });
    data.value = body;
    loadedSortMetric.value = filters.sort_metric;
  } catch (error) {
    errorText.value = t('QA_DASHBOARD.TRENDS.ERROR_LOAD');
    useAlert(errorText.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  load();
});
</script>

<template>
  <div class="qa-page" :data-qa-compile-marker="compileMarker">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.TRENDS.TITLE') }}</h1>
          <p class="qa-desc">{{ t('QA_DASHBOARD.TRENDS.DESCRIPTION') }}</p>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="load"
          >
            {{ t('QA_DASHBOARD.TRENDS.REFRESH') }}
          </button>
        </div>
      </div>

      <div class="qa-pill qa-pill--muted">
        {{ t('QA_DASHBOARD.TRENDS.OBSERVE_NOTE') }}
      </div>

      <div class="qa-toolbar">
        <div class="qa-toolbar-left">
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.TRENDS.WINDOW_START')
            }}</span>
            <input
              v-model="filters.window_start"
              type="datetime-local"
              class="qa-input"
            />
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.TRENDS.WINDOW_END')
            }}</span>
            <input
              v-model="filters.window_end"
              type="datetime-local"
              class="qa-input"
            />
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.TRENDS.REPORT_KIND')
            }}</span>
            <select
              v-model="filters.report_kind"
              class="qa-select"
              :disabled="isLoading"
              @change="load"
            >
              <option value="overview">
                {{ t('QA_DASHBOARD.TRENDS.KIND_OVERVIEW') }}
              </option>
              <option value="deep">
                {{ t('QA_DASHBOARD.TRENDS.KIND_DEEP') }}
              </option>
            </select>
          </label>
          <button
            type="button"
            class="qa-btn qa-btn--primary"
            :disabled="isLoading"
            @click="load"
          >
            {{ t('QA_DASHBOARD.TRENDS.APPLY') }}
          </button>
        </div>
      </div>

      <p v-if="errorText" class="qa-error">{{ errorText }}</p>

      <template v-if="data">
        <p v-if="daysInWindow" class="qa-hint">
          {{
            t('QA_DASHBOARD.TRENDS.DAY_COVERAGE', {
              covered: series.length,
              total: daysInWindow,
            })
          }}
        </p>

        <!-- ── Daily trends ── -->
        <div v-if="!series.length" class="qa-card">
          <div class="qa-card-body">
            <h3 class="qa-card-title">
              {{ t('QA_DASHBOARD.TRENDS.TRENDS_EMPTY_TITLE') }}
            </h3>
            <p class="qa-desc">
              {{ t('QA_DASHBOARD.TRENDS.TRENDS_EMPTY_BODY') }}
            </p>
          </div>
        </div>

        <template v-else>
          <!-- latest-day headline tiles -->
          <div v-if="latest" class="qa-subheader">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.TRENDS.SECTION_LATEST') }}
            </h2>
            <span class="qa-subtle">{{ latest.business_date }}</span>
          </div>
          <div v-if="latest" class="qa-kpis">
            <!-- Tones follow the reviewer's words, not the detector's scoreboard:
                 green on "Confirmed problem" said the machine had been right,
                 which now reads as good news about a real problem. -->
            <div class="qa-kpi qa-kpi--tone-bad">
              <span class="qa-kpi-label">{{
                t('QA_DASHBOARD.TRENDS.M_REVIEW_CONFIRMED')
              }}</span>
              <span class="qa-kpi-value">{{
                fmtVal(latest.metrics.review_confirmed, 'num')
              }}</span>
            </div>
            <div class="qa-kpi qa-kpi--tone-warn">
              <span class="qa-kpi-label">{{
                t('QA_DASHBOARD.TRENDS.M_REVIEW_PENDING')
              }}</span>
              <span class="qa-kpi-value">{{
                fmtVal(latest.metrics.review_pending, 'num')
              }}</span>
            </div>
            <div class="qa-kpi qa-kpi--tone-neutral">
              <span class="qa-kpi-label">{{
                t('QA_DASHBOARD.TRENDS.M_REVIEW_PASSED')
              }}</span>
              <span class="qa-kpi-value">{{
                fmtVal(latest.metrics.review_passed, 'num')
              }}</span>
            </div>
            <div class="qa-kpi qa-kpi--tone-accent">
              <span class="qa-kpi-label">{{
                t('QA_DASHBOARD.TRENDS.M_ANSWER_ACCURACY')
              }}</span>
              <span class="qa-kpi-value">{{
                fmtVal(latest.metrics.answer_accuracy_rate, 'pct')
              }}</span>
              <span v-if="accuracyDenominator(latest)" class="qa-kpi-suffix">
                {{
                  t('QA_DASHBOARD.TRENDS.M_ANSWER_ACCURACY_OF', {
                    correct: latest.metrics.answer_accuracy_correct ?? 0,
                    eligible: accuracyDenominator(latest),
                  })
                }}
              </span>
            </div>
            <div class="qa-kpi qa-kpi--tone-neutral">
              <span class="qa-kpi-label">{{
                t('QA_DASHBOARD.TRENDS.M_CUSTOMER_TURNS')
              }}</span>
              <span class="qa-kpi-value">{{
                fmtVal(latest.metrics.customer_turns, 'num')
              }}</span>
            </div>
          </div>

          <p v-if="latest" class="qa-hint">
            {{ t('QA_DASHBOARD.TRENDS.ANSWER_ACCURACY_NOTE') }}
          </p>

          <!-- review-outcomes trend chart (hero visual) -->
          <div class="qa-card">
            <div class="qa-card-header">
              <h3 class="qa-card-title">
                {{ t('QA_DASHBOARD.TRENDS.SECTION_REVIEW_CHART') }}
              </h3>
              <span class="qa-subtle">{{
                t('QA_DASHBOARD.TRENDS.CHART_ACCUMULATE')
              }}</span>
            </div>
            <div class="qa-card-body">
              <div class="qa-chart-frame">
                <LineChart
                  :collection="reviewChart"
                  :chart-options="lineOptions"
                />
              </div>
            </div>
          </div>

          <!-- full detail table (kept as the accessible table view) -->
          <details class="qa-details">
            <summary>{{ t('QA_DASHBOARD.TRENDS.SECTION_TRENDS') }}</summary>
            <div class="qa-table-wrap" style="margin-top: 8px">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.TRENDS.TH_DATE') }}
                    </th>
                    <th
                      v-for="col in TREND_COLS"
                      :key="col.key"
                      class="qa-th qa-th--center"
                    >
                      {{ t(`QA_DASHBOARD.TRENDS.${col.label}`) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in series" :key="row.business_date">
                    <td class="qa-td">{{ row.business_date }}</td>
                    <td
                      v-for="col in TREND_COLS"
                      :key="col.key"
                      class="qa-td qa-th--center"
                    >
                      {{ fmtVal(row.metrics[col.key], col.fmt) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </template>

        <!-- ── Agent leaderboard ── -->
        <div class="qa-subheader" style="margin-top: 16px">
          <h2 class="qa-card-title">
            {{ t('QA_DASHBOARD.TRENDS.SECTION_LEADERBOARD') }}
          </h2>
        </div>

        <div v-if="isScoped" class="qa-pill qa-pill--warn">
          {{ t('QA_DASHBOARD.TRENDS.SCOPED_CAVEAT') }}
        </div>

        <div class="qa-toolbar">
          <div class="qa-toolbar-left">
            <label class="qa-field">
              <span class="qa-label">{{
                t('QA_DASHBOARD.TRENDS.LB_SORT')
              }}</span>
              <select
                v-model="filters.sort_metric"
                class="qa-select"
                :disabled="isLoading"
                @change="load"
              >
                <option v-for="opt in sortOptions" :key="opt" :value="opt">
                  {{ t(`QA_DASHBOARD.TRENDS.SORT_${opt}`) }}
                </option>
              </select>
            </label>
          </div>
        </div>

        <div v-if="isEmptyScope" class="qa-card">
          <div class="qa-card-body">
            <h3 class="qa-card-title">
              {{ t('QA_DASHBOARD.TRENDS.EMPTY_SCOPE_TITLE') }}
            </h3>
            <p class="qa-desc">
              {{ t('QA_DASHBOARD.TRENDS.EMPTY_SCOPE_BODY') }}
            </p>
          </div>
        </div>
        <div v-else-if="!lbRows.length" class="qa-card">
          <div class="qa-card-body">
            <h3 class="qa-card-title">
              {{ t('QA_DASHBOARD.TRENDS.LB_EMPTY_TITLE') }}
            </h3>
            <p class="qa-desc">{{ t('QA_DASHBOARD.TRENDS.LB_EMPTY_BODY') }}</p>
          </div>
        </div>
        <div v-else-if="!selectedMetricHasData" class="qa-card">
          <div class="qa-card-body">
            <h3 class="qa-card-title">
              {{ t('QA_DASHBOARD.TRENDS.LB_METRIC_EMPTY_TITLE') }}
            </h3>
            <p class="qa-desc">
              {{
                t('QA_DASHBOARD.TRENDS.LB_METRIC_EMPTY_BODY', {
                  agents: lbRows.length,
                })
              }}
            </p>
            <p v-if="metricsWithData.length" class="qa-hint">
              {{ t('QA_DASHBOARD.TRENDS.LB_METRIC_SUGGEST') }}
              {{
                metricsWithData
                  .map(metric => t(`QA_DASHBOARD.TRENDS.SORT_${metric}`))
                  .join(' · ')
              }}
            </p>
          </div>
        </div>
        <template v-else>
          <div class="qa-card">
            <div class="qa-card-header">
              <h3 class="qa-card-title">
                {{ t(`QA_DASHBOARD.TRENDS.SORT_${filters.sort_metric}`) }}
              </h3>
              <span class="qa-subtle">{{
                t('QA_DASHBOARD.TRENDS.LB_CHART_NOTE')
              }}</span>
            </div>
            <div class="qa-card-body">
              <div class="qa-chart-frame">
                <BarChart :collection="lbChart" :chart-options="lbOptions" />
              </div>
            </div>
          </div>

          <details class="qa-details">
            <summary>
              {{ t('QA_DASHBOARD.TRENDS.SECTION_LEADERBOARD') }}
            </summary>
            <div class="qa-table-wrap" style="margin-top: 8px">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.TRENDS.LB_RANK') }}
                    </th>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.TRENDS.LB_AGENT') }}
                    </th>
                    <th
                      v-for="col in LB_COLS"
                      :key="col.key"
                      class="qa-th qa-th--center"
                    >
                      {{ t(`QA_DASHBOARD.TRENDS.${col.label}`) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in lbRows" :key="row.agent_user_id">
                    <td class="qa-td qa-th--center">{{ row.rank }}</td>
                    <td class="qa-td">{{ agentName(row.agent_user_id) }}</td>
                    <td
                      v-for="col in LB_COLS"
                      :key="col.key"
                      class="qa-td qa-th--center"
                    >
                      {{ fmtVal(row.metrics[col.key], col.fmt) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.qa-chart-frame {
  height: 300px;
  width: 100%;
}

/* qaShell.css lays this page out as full-bleed bands (.qa-header, .qa-subheader
   and .qa-toolbar each carry their own background, rule and 24px of internal
   padding) alternating with content blocks. The bands were always inset; the
   content blocks hang straight off .qa-shell and so started at the card border,
   which is what made the page look like it had no margin at all.

   Every other QA page takes this inset from a .qa-content wrapper, but a single
   wrapper cannot express it here: the bands sit between the content blocks and
   have to stay full width. Hence the same 32px applied directly. Vue renders no
   element for <template v-if>, so these really are direct children of .qa-shell
   in the DOM. */
.qa-shell > .qa-pill,
.qa-shell > .qa-error,
.qa-shell > .qa-kpis,
.qa-shell > .qa-hint,
.qa-shell > .qa-card,
.qa-shell > .qa-details {
  margin-left: 32px;
  margin-right: 32px;
}

/* Both notes follow a band and had no top margin, so each sat directly on the
   rule that closes it — the page header above the first, the leaderboard
   subheader above the second. 16px is what .qa-content puts between that rule
   and a page's first block everywhere else. Below each one a toolbar
   contributes its own 12px of padding, so 4px here brings that side to a
   matching 16px; the 12px these carried inline made it 24px. */
.qa-shell > .qa-pill {
  margin-top: 16px;
  margin-bottom: 4px;
}

/* Same breakpoint qaShell.css uses to pull .qa-content and the bands' padding
   in to 16px, so the two stay aligned on narrow screens. */
@media (max-width: 1100px) {
  .qa-shell > .qa-pill,
  .qa-shell > .qa-error,
  .qa-shell > .qa-kpis,
  .qa-shell > .qa-hint,
  .qa-shell > .qa-card,
  .qa-shell > .qa-details {
    margin-left: 16px;
    margin-right: 16px;
  }
}
</style>
